import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { useCall } from "context/CallContext"

import MicOffIcon from "@mui/icons-material/MicOff"
import MicIcon from "@mui/icons-material/Mic"
import CallEndIcon from "@mui/icons-material/CallEnd"
import VideocamIcon from "@mui/icons-material/Videocam"
import VideocamOffIcon from "@mui/icons-material/VideocamOff"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"
import PersonIcon from "@mui/icons-material/Person"
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom"

const GlobalCallOverlay = () => {
  const history = useHistory()
  const location = useLocation()

  const {
    joined,
    isPiP,
    pipPos,
    setIsPiP,
    setPipPos,
    channelName,
    callStarted,
    callDuration,
    isMuted,
    isVideoOff,
    remoteUsers,
    isVideoCall,
    callViewMode,
    setCallViewMode,
    setPendingRestore,
    leaveCall,
    toggleMic,
    toggleVideo,
    waitAndReplay,
  } = useCall()

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  const dragging = useRef(false)
  const dragOffset = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const isPipMode = callViewMode === "minimized"
  const isAudioCall = !isVideoCall

  // The fullscreen layout is handled by VideoChat directly on the prescription page.
  const allowOverlay = joined && callViewMode === "minimized"

  const formatCallDuration = seconds => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0")
    const secs = String(seconds % 60).padStart(2, "0")
    return `${mins}:${secs}`
  }

  const pipSize = useMemo(() => {
    if (windowSize.width < 576) {
      return { width: Math.max(300, windowSize.width - 24), height: 330 }
    }
    return { width: 480, height: 390 }
  }, [windowSize.width])

  const pipStyle = useMemo(() => {
    const maxX = Math.max(12, windowSize.width - pipSize.width - 12)
    const maxY = Math.max(12, windowSize.height - pipSize.height - 12)
    return {
      ...styles.pipContainer,
      left: Math.max(12, Math.min(pipPos.x, maxX)),
      top: Math.max(12, Math.min(pipPos.y, maxY)),
      width: pipSize.width,
      height: pipSize.height,
    }
  }, [pipPos, pipSize, windowSize.width, windowSize.height])

  // ── Restore from minimized back to original ────────────────────────────────
  const handleRestore = useCallback(() => {
    if (!channelName) return

    const prescriptionPath = `/prescription/${channelName}`

    setPendingRestore(true)

    if (!location.pathname.includes("/prescription")) {
      history.push(prescriptionPath)
    }

    // Wait for route + DOM to settle, then restore and replay
    setTimeout(() => {
      setCallViewMode("original")
      setIsPiP(false)
      setPendingRestore(false)

      // Trigger retry-loop replay after view mode is set
      setTimeout(() => {
        waitAndReplay()
      }, 150)
    }, 300)
  }, [
    channelName,
    history,
    location.pathname,
    setCallViewMode,
    setIsPiP,
    setPendingRestore,
    waitAndReplay,
  ])

  // ── Auto-minimize when navigating away from prescription page ─────────────
  useEffect(() => {
    // debug: log when evaluating auto-minimize conditions
    console.log("[GlobalCallOverlay] auto-minimize check", {
      joined,
      remoteCount: remoteUsers.length,
      callViewMode,
      pathname: location.pathname,
    })

    if (
      joined &&
      remoteUsers.length > 0 &&
      callViewMode === "original" &&
      !location.pathname.includes("/prescription")
    ) {
      console.log("[GlobalCallOverlay] auto-minimizing call")
      setCallViewMode("minimized")
      // ensure PiP mode is enabled
      try { setIsPiP(true) } catch (e) { /* ignore if not available */ }
    }
  }, [joined, remoteUsers.length, callViewMode, location.pathname, setCallViewMode])

  // ── Drag handlers ──────────────────────────────────────────────────────────
  const onMouseDown = useCallback(
    event => {
      if (event.target.closest("button")) return
      dragging.current = true
      dragOffset.current = {
        x: event.clientX - pipPos.x,
        y: event.clientY - pipPos.y,
      }
      event.preventDefault()
    },
    [pipPos]
  )

  const onTouchStart = useCallback(
    event => {
      if (event.target.closest("button")) return
      const touch = event.touches[0]
      dragging.current = true
      dragOffset.current = {
        x: touch.clientX - pipPos.x,
        y: touch.clientY - pipPos.y,
      }
    },
    [pipPos]
  )

  useEffect(() => {
    const onMouseMove = event => {
      if (!dragging.current) return
      const maxX = Math.max(12, windowSize.width - pipSize.width - 12)
      const maxY = Math.max(12, windowSize.height - pipSize.height - 12)
      setPipPos({
        x: Math.max(12, Math.min(maxX, event.clientX - dragOffset.current.x)),
        y: Math.max(12, Math.min(maxY, event.clientY - dragOffset.current.y)),
      })
    }

    const onTouchMove = event => {
      if (!dragging.current) return
      const touch = event.touches[0]
      const maxX = Math.max(12, windowSize.width - pipSize.width - 12)
      const maxY = Math.max(12, windowSize.height - pipSize.height - 12)
      setPipPos({
        x: Math.max(12, Math.min(maxX, touch.clientX - dragOffset.current.x)),
        y: Math.max(12, Math.min(maxY, touch.clientY - dragOffset.current.y)),
      })
    }

    const stopDragging = () => { dragging.current = false }

    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", stopDragging)
    window.addEventListener("touchmove", onTouchMove)
    window.addEventListener("touchend", stopDragging)

    return () => {
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", stopDragging)
      window.removeEventListener("touchmove", onTouchMove)
      window.removeEventListener("touchend", stopDragging)
    }
  }, [setPipPos, windowSize.width, windowSize.height, pipSize])

  if (!allowOverlay) return null

  // ── FIX: fullscreen mode — NO second VideoChat rendered here.
  // VideoChat on the prescription page handles fullscreen itself via
  // position:fixed. This overlay only shows a minimal top bar for controls.
  if (callViewMode === "fullscreen") {
    return null
  }

  // ── Minimized PiP ──────────────────────────────────────────────────────────
  return (
    <div onMouseDown={onMouseDown} onTouchStart={onTouchStart} style={pipStyle}>
      {/* Header */}
      <div style={styles.pipHeader}>
        <span style={styles.pipTitle}>
          {isAudioCall ? "📞" : "📹"}{" "}
          {callStarted ? formatCallDuration(callDuration) : "Waiting..."}
        </span>

        <div style={styles.pipHeaderButtons}>
          <button onClick={handleRestore} style={styles.pipIconBtn} title="Restore">
            <OpenInFullIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>

      {/* Body */}
      {isAudioCall ? (
        <div style={styles.pipBody}>
          <div style={styles.pipAudioArea}>
            <div style={styles.pipAudioAvatar}>
              <PersonIcon style={{ fontSize: 42, color: "#fff" }} />
            </div>
            <span style={styles.pipAudioStatus}>
              {remoteUsers.length > 0 ? "Connected" : "Waiting..."}
            </span>
          </div>
        </div>
      ) : (
        <div style={styles.pipBody}>
          {remoteUsers.length > 0 ? (
            remoteUsers.map(user => (
              <div
                key={user.uid}
                id={`pip-remote-video-${user.uid}`}
                style={styles.pipRemoteVideo}
              />
            ))
          ) : (
            <div style={styles.pipWaiting}>
              <HourglassBottomIcon style={{ fontSize: 32, color: "#aaa" }} />
              <span style={styles.pipWaitingText}>Waiting...</span>
            </div>
          )}

          {!isVideoOff ? (
            <div id="pip-local-video" style={styles.pipLocalVideo} />
          ) : (
            <div style={{ ...styles.pipLocalVideo, ...styles.pipLocalVideoOff }}>
              <VideocamOffIcon style={{ fontSize: 20, color: "#aaa" }} />
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      <div style={styles.pipControls}>
        <button
          onClick={toggleMic}
          style={{
            ...styles.pipControlBtn,
            backgroundColor: isMuted ? "#e53935" : "rgba(255,255,255,0.15)",
          }}
          title={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted
            ? <MicOffIcon style={{ fontSize: 20 }} />
            : <MicIcon style={{ fontSize: 20 }} />}
        </button>

        {!isAudioCall && (
          <button
            onClick={toggleVideo}
            style={{
              ...styles.pipControlBtn,
              backgroundColor: isVideoOff ? "#e53935" : "rgba(255,255,255,0.15)",
            }}
            title={isVideoOff ? "Turn on camera" : "Turn off camera"}
          >
            {isVideoOff
              ? <VideocamOffIcon style={{ fontSize: 20 }} />
              : <VideocamIcon style={{ fontSize: 20 }} />}
          </button>
        )}

        <button onClick={leaveCall} style={styles.pipEndBtn} title="End call">
          <CallEndIcon style={{ fontSize: 20 }} />
        </button>
      </div>
    </div>
  )
}

const styles = {
  pipContainer: {
    position: "fixed",
    borderRadius: 24,
    backgroundColor: "#1a1a1a",
    boxShadow: "0 16px 45px rgba(0,0,0,0.45)",
    border: "1px solid rgba(255,255,255,0.12)",
    zIndex: 999999,
    overflow: "hidden",
    cursor: "grab",
    userSelect: "none",
    touchAction: "none",
    display: "flex",
    flexDirection: "column",
  },
  pipHeader: {
    height: 60,
    minHeight: 60,
    padding: "0 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#000",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  pipTitle: {
    color: "#fff",
    fontSize: 18,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  pipHeaderButtons: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  pipIconBtn: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "none",
    backgroundColor: "rgba(255,255,255,0.16)",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pipBody: {
    position: "relative",
    flex: 1,
    backgroundColor: "#1c1c1c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  pipRemoteVideo: {
    width: "100%",
    height: "100%",
    backgroundColor: "#111",
  },
  pipWaiting: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  pipWaitingText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: 600,
  },
  pipLocalVideo: {
    position: "absolute",
    right: 18,
    bottom: 18,
    width: 110,
    height: 140,
    borderRadius: 14,
    overflow: "hidden",
    backgroundColor: "#333",
    zIndex: 10,
    border: "2px solid rgba(255,255,255,0.25)",
  },
  pipLocalVideoOff: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pipAudioArea: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    backgroundColor: "#1a1a2e",
  },
  pipAudioAvatar: {
    width: 90,
    height: 90,
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pipAudioStatus: {
    color: "#fff",
    fontSize: 18,
    fontWeight: 600,
  },
  pipControls: {
    height: 70,
    minHeight: 70,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
    backgroundColor: "#000",
    paddingBottom: 4,
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  pipControlBtn: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    border: "none",
    color: "#fff",
    backgroundColor: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  pipEndBtn: {
    width: 54,
    height: 54,
    borderRadius: "50%",
    border: "none",
    color: "#fff",
    backgroundColor: "#e53935",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  // Fullscreen: 
 fullscreenWrapper: {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1000001,
  backgroundColor: "#000",
  display: "flex",
  flexDirection: "column",
},
fullscreenBody: {
  position: "relative",
  flex: 1,
  backgroundColor: "#1c1c1c",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
},
fullscreenRemoteVideo: {
  width: "100%",
  height: "100%",
  backgroundColor: "#111",
},
fullscreenLocalVideo: {
  position: "absolute",
  right: 24,
  bottom: 24,
  width: 160,
  height: 200,
  borderRadius: 14,
  overflow: "hidden",
  backgroundColor: "#333",
  zIndex: 10,
  border: "2px solid rgba(255,255,255,0.25)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
},
}

export default GlobalCallOverlay